import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Gavel, Upload, FolderOpen, FileText, ScrollText, CheckCircle2, Pencil,
  XCircle, ScaleIcon, GitCompareArrows, ShieldCheck, Loader2, RefreshCw,
  ArrowLeft, AlertCircle, FileSearch,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { JusticeThinking } from "@/components/Justice";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_META = {
  needs_review: { label: "Needs Review", color: "#E4B34A", bg: "rgba(228,179,74,0.12)" },
  confirmed: { label: "Confirmed", color: "var(--ek-secure)", bg: "rgba(16,185,129,0.12)" },
  rejected: { label: "Not part of this order", color: "var(--ek-text-muted)", bg: "rgba(148,163,184,0.1)" },
};

function ProvisionRow({ p, onUpdate, busy }) {
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState(p.type || "");
  const [value, setValue] = useState(p.value || "");
  const meta = STATUS_META[p.status] || STATUS_META.needs_review;

  const save = async () => {
    await onUpdate(p.id, { type, value });
    setEditing(false);
  };

  return (
    <div
      className="border rounded-lg p-4"
      style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }}
      data-testid={`provision-row-${p.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className="ek-mono-label" style={{ color: "var(--ek-primary)" }}>{p.type || "Provision"}</span>
        <span className="ek-stamp shrink-0" style={{ color: meta.color, borderColor: meta.color, background: meta.bg }}>
          {meta.label}
        </span>
      </div>

      {editing ? (
        <div className="space-y-2">
          <Input
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="Provision label"
            data-testid={`provision-type-input-${p.id}`}
            style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}
          />
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            data-testid={`provision-value-input-${p.id}`}
            style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy} data-testid={`provision-save-${p.id}`} className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setType(p.type); setValue(p.value); }} className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm leading-relaxed" data-private style={{ color: "var(--ek-text)" }}>{p.value}</p>
      )}

      {!editing && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t" style={{ borderColor: "var(--ek-border)" }}>
          <Button
            size="sm" variant="outline" disabled={busy || p.status === "confirmed"}
            onClick={() => onUpdate(p.id, { status: "confirmed" })}
            data-testid={`provision-confirm-${p.id}`}
            className="rounded-md" style={{ borderColor: "rgba(16,185,129,0.4)", background: "transparent", color: "var(--ek-secure)" }}
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Confirm
          </Button>
          <Button
            size="sm" variant="outline" disabled={busy}
            onClick={() => setEditing(true)}
            data-testid={`provision-edit-${p.id}`}
            className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
          </Button>
          <Button
            size="sm" variant="outline" disabled={busy || p.status === "rejected"}
            onClick={() => onUpdate(p.id, { status: "rejected" })}
            data-testid={`provision-reject-${p.id}`}
            className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-text-muted)" }}
          >
            <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
          </Button>
        </div>
      )}
    </div>
  );
}

function VaultPickerDialog({ open, onOpenChange, evidence, onPick }) {
  const docs = (evidence || []).filter((e) => ["pdf", "document", "photo"].includes(e.format));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="court-order-vault-picker">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight flex items-center gap-2">
            <FolderOpen className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Select from your Vault
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            Choose the document that contains your governing court order.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {docs.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: "var(--ek-text-muted)" }}>
              No documents in your Vault yet. Upload the order instead.
            </p>
          ) : docs.map((e) => (
            <button
              key={e.id}
              onClick={() => onPick(e.id)}
              data-testid={`vault-pick-${e.id}`}
              className="w-full flex items-center gap-3 text-left rounded-md border px-3 py-2.5 transition-colors hover:border-[var(--ek-primary)]"
              style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}
            >
              <FileText className="h-4 w-4 shrink-0" style={{ color: "var(--ek-primary)" }} />
              <span className="text-sm flex-1 min-w-0 truncate" data-private>{e.filename || "Untitled"}</span>
              <span className="ek-mono-label shrink-0">{(e.format || "").toUpperCase()}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CourtOrder({ matterId, evidence, activeMatter, onNav }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [consistency, setConsistency] = useState(null);
  const [checking, setChecking] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrder(data);
    } catch { setOrder({ order_evidence_id: null, provisions: [] }); }
    finally { setLoading(false); }
    // matterId kept as a cache key so the order reloads when the active case changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId]);

  useEffect(() => { load(); setConsistency(null); setShowSetup(false); }, [load]);

  const setOrderEvidence = async (evidenceId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders/set", { evidence_id: evidenceId });
      toast.success(
        data.extracted > 0
          ? `Justice found ${data.extracted} provision${data.extracted === 1 ? "" : "s"} to review`
          : "Order saved. No provisions were auto-detected — you can review the document manually."
      );
      setShowSetup(false);
      setPickerOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set the court order");
    } finally { setBusy(false); }
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "Court Order");
      if (matterId) fd.append("matter_id", matterId);
      const { data: up } = await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const evId = up?.id || (up?.records && up.records[0]?.id) || (up?.added && up.added[0]?.id);
      if (!evId) { toast.error("Upload succeeded but the file id was not returned"); return; }
      await setOrderEvidence(evId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const updateProvision = async (pid, patch) => {
    setBusy(true);
    try {
      await api.put(`/orders/provisions/${pid}`, patch);
      setOrder((o) => ({
        ...o,
        provisions: o.provisions.map((p) => (p.id === pid ? { ...p, ...patch } : p)),
      }));
      setConsistency(null);
    } catch { toast.error("Could not update the provision"); }
    finally { setBusy(false); }
  };

  const runConsistency = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/orders/consistency");
      setConsistency(data);
    } catch { toast.error("Could not run the Order Consistency Check"); }
    finally { setChecking(false); }
  };

  if (!matterId) {
    return (
      <div className="border rounded-lg p-10 text-center max-w-md mx-auto" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="court-order-no-matter">
        <ScaleIcon className="h-10 w-10 mx-auto mb-4" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.3} />
        <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>Select or create a case first to add a court order.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--ek-primary)" }} /></div>;
  }

  const hasOrder = !!order?.order_evidence_id;
  const provisions = order?.provisions || [];
  const confirmedCount = provisions.filter((p) => p.status === "confirmed").length;

  // Entry point: no order yet, or user chose to change it
  if (!hasOrder || showSetup) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="court-order-setup">
        {showSetup && (
          <button onClick={() => setShowSetup(false)} className="flex items-center gap-1.5 text-sm" style={{ color: "var(--ek-primary)" }} data-testid="court-order-setup-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="border rounded-lg p-8 text-center ek-scanline" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          <div className="h-14 w-14 mx-auto mb-4 rounded-lg flex items-center justify-center" style={{ background: "var(--ek-page)", border: "1px solid var(--ek-border)" }}>
            <Gavel className="h-7 w-7" style={{ color: "var(--ek-primary)" }} />
          </div>
          <h2 className="font-head font-bold text-2xl tracking-tight mb-2">Do you have an existing court order?</h2>
          <p className="text-sm mb-8 max-w-md mx-auto" style={{ color: "var(--ek-text-secondary)" }}>
            Add your custody order, decree, or agreement and Justice will help you organize its provisions.
            EvidenceKeeper documents facts — it does not make legal conclusions.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || busy}
              data-testid="court-order-upload-btn"
              className="group flex flex-col items-center gap-2 rounded-lg border p-6 transition-colors hover:border-[var(--ek-primary)] disabled:opacity-60"
              style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}
            >
              {uploading ? <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--ek-primary)" }} /> : <Upload className="h-6 w-6" style={{ color: "var(--ek-primary)" }} />}
              <span className="font-head font-semibold text-sm">{uploading ? "Uploading…" : "Upload a new file"}</span>
              <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>PDF, image or document</span>
            </button>
            <button
              onClick={() => setPickerOpen(true)}
              disabled={uploading || busy}
              data-testid="court-order-select-vault-btn"
              className="group flex flex-col items-center gap-2 rounded-lg border p-6 transition-colors hover:border-[var(--ek-primary)] disabled:opacity-60"
              style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}
            >
              <FolderOpen className="h-6 w-6" style={{ color: "var(--ek-primary)" }} />
              <span className="font-head font-semibold text-sm">Select from Vault</span>
              <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>Use an existing document</span>
            </button>
          </div>
          {busy && <div className="mt-4 flex justify-center"><JusticeThinking size={40} compact messages={["Justice is reading the order…", "Extracting the provisions…", "Organizing the terms…"]} testid="order-reading-processing" /></div>}
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*,.doc,.docx"
            className="hidden"
            data-testid="court-order-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onUploadFile(f); }}
          />
        </motion.div>
        <VaultPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} evidence={evidence} onPick={setOrderEvidence} />
      </div>
    );
  }

  const orderFile = (evidence || []).find((e) => e.id === order.order_evidence_id);

  return (
    <div className="space-y-8" data-testid="court-order-view">
      {/* Header */}
      <div className="border rounded-lg p-5 sm:p-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--ek-primary)" }}>
              <Gavel className="h-5 w-5 text-white" />
            </span>
            <div>
              <h1 className="font-head font-bold text-xl tracking-tight">Governing Court Order</h1>
              <p className="text-sm mt-0.5 flex items-center gap-1.5" style={{ color: "var(--ek-text-secondary)" }}>
                <FileText className="h-3.5 w-3.5" /> <span data-private className="truncate max-w-[240px]">{orderFile?.filename || "Order document"}</span>
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setShowSetup(true); setConsistency(null); }} data-testid="court-order-change-btn" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Change order
          </Button>
        </div>
      </div>

      {/* Provision review */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ScrollText className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
            <h2 className="font-head font-semibold tracking-tight">Order Provisions</h2>
          </div>
          <span className="ek-mono-label">{confirmedCount} of {provisions.length} confirmed</span>
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--ek-text-muted)" }}>
          Review each auto-detected provision. Confirm the ones that are accurate, edit any wording, or reject anything that isn't part of your order.
        </p>
        {provisions.length === 0 ? (
          <div className="border rounded-lg p-8 text-center" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="court-order-no-provisions">
            <FileSearch className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.3} />
            <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>
              No provisions were auto-detected in this document. You can re-select the order or add timeline entries and check documentation coverage below.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {provisions.map((p) => (
              <ProvisionRow key={p.id} p={p} onUpdate={updateProvision} busy={busy} />
            ))}
          </div>
        )}
      </div>

      {/* Consistency report */}
      <div className="border rounded-lg p-5 sm:p-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="consistency-section">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <GitCompareArrows className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
            <h2 className="font-head font-semibold tracking-tight">Order Consistency Check</h2>
          </div>
          <Button size="sm" onClick={runConsistency} disabled={checking} data-testid="run-consistency-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {checking ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Comparing…</> : <><GitCompareArrows className="h-3.5 w-3.5 mr-1.5" /> Run check</>}
          </Button>
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--ek-text-muted)" }}>
          Compares your <b>confirmed</b> provisions against your documented timeline events. Results describe factual differences and documentation gaps only.
        </p>

        {checking && !consistency && (
          <div className="py-6"><JusticeThinking size={64} messages={["Comparing confirmed order provisions…", "Organizing the timeline…", "Linking facts to their sources…", "Checking documentation coverage…"]} testid="consistency-processing" /></div>
        )}

        {consistency && (
          <div className="space-y-5" data-testid="consistency-result">
            {/* Coverage */}
            {consistency.coverage && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="consistency-coverage">
                {[
                  { t: "Documented Events", v: consistency.coverage.documented_events },
                  { t: "Linked to Sources", v: consistency.coverage.linked_to_sources },
                  { t: "Manual Only", v: consistency.coverage.manual_only },
                  { t: "Confirmed Provisions", v: consistency.coverage.confirmed_provisions },
                ].map((x) => (
                  <div key={x.t} className="rounded-md border p-3 text-center" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
                    <div className="text-2xl font-head font-bold" style={{ color: "var(--ek-primary)" }}>{x.v ?? 0}</div>
                    <div className="ek-mono-label mt-1">{x.t}</div>
                  </div>
                ))}
              </div>
            )}

            {consistency.message && (
              <div className="flex items-start gap-2 rounded-md border p-3 text-sm" style={{ borderColor: "rgba(228,179,74,0.35)", background: "rgba(228,179,74,0.08)", color: "var(--ek-text-secondary)" }} data-testid="consistency-message">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#E4B34A" }} /> {consistency.message}
              </div>
            )}

            {/* Findings */}
            {(consistency.findings || []).length > 0 && (
              <div className="space-y-3" data-testid="consistency-findings">
                {consistency.findings.map((f, i) => {
                  const isGap = !f.documented_difference;
                  return (
                    <div key={i} className="border rounded-lg p-4" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} data-testid={`consistency-finding-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="ek-stamp" style={{ color: isGap ? "var(--ek-text-muted)" : "#E4B34A", borderColor: isGap ? "var(--ek-border)" : "rgba(228,179,74,0.4)", background: isGap ? "transparent" : "rgba(228,179,74,0.1)" }}>
                          {isGap ? "Documentation Gap" : "Documented Difference"}
                        </span>
                      </div>
                      <p className="text-sm font-medium mb-1" data-private style={{ color: "var(--ek-text)" }}>{f.provision}</p>
                      {f.documented_difference && <p className="text-sm mb-1" data-private style={{ color: "var(--ek-text-secondary)" }}>{f.documented_difference}</p>}
                      {f.note && <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>{f.note}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {consistency.disclaimer && (
              <div className="flex items-center gap-2 text-xs pt-2 border-t" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text-muted)" }}>
                <ShieldCheck className="h-3.5 w-3.5" style={{ color: "var(--ek-secure)" }} /> {consistency.disclaimer}
              </div>
            )}
          </div>
        )}
      </div>

      <VaultPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} evidence={evidence} onPick={setOrderEvidence} />
    </div>
  );
}
